import { useState, useRef, useEffect } from 'react';
import { Popover } from 'antd';
import { SmartCarrierMatch, InformationIcon, SpinnerIcon } from '@/components';
import { Carrier, CarrierPreviewItem, ServiceTypeEnum } from '@/types';
import { useCarrierPreviewByOrderExcelRow, useCarrierPreviewByOrderId } from '@/hooksApi';
import { useTranslation } from 'react-i18next';
import { normalizeToTranslationKey } from '@/utils';

interface CarrierHoverCellProps {
  orderId?: string;
  carrier: Carrier | null;
  rowUid?: string;
  pickupAddressRegionUid?: string;
}

const NoCarrierMatchesExceptionError = 889000;

const CarrierHoverCell: React.FC<CarrierHoverCellProps> = ({
  orderId,
  carrier,
  rowUid,
  pickupAddressRegionUid,
}) => {
  const { t } = useTranslation();
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasCalledApiRef = useRef(false);
  const lastRequestKeyRef = useRef<string | null>(null);

  const { mutate: fetchCarrierPreviewByOrderId, isLoading: carrierPreviewLoadingByOrderId } =
    useCarrierPreviewByOrderId();
  const { mutate: fetchCarrierPreviewByRowId, isLoading: carrierPreviewLoadingByRowId } =
    useCarrierPreviewByOrderExcelRow();
  const [carrierPreviewData, setCarrierPreviewData] = useState<CarrierPreviewItem[] | undefined>(
    undefined,
  );
  const [noCarrierAssigned, setNoCarrierAssigned] = useState(false);

  const requestKey = orderId
    ? `order:${orderId}`
    : rowUid && pickupAddressRegionUid
      ? `excel:${rowUid}:${pickupAddressRegionUid}`
      : 'none';

  const handleMouseEnter = () => {
    setIsHovering(true);

    // If carrier exists, show popup immediately without delay
    if (carrier) {
      setIsPopupVisible(true);
      return;
    }

    // If no carrier, start 1-second timer before showing popup
    hoverTimeoutRef.current = setTimeout(() => {
      setIsPopupVisible(true);
    }, 1000);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);

    // Clear timeout if user leaves before delay completes
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    // Close popup immediately if it's visible
    setIsPopupVisible(false);
  };

  useEffect(() => {
    if (!isPopupVisible || carrier || requestKey === 'none') return;

    if (lastRequestKeyRef.current !== requestKey) {
      lastRequestKeyRef.current = requestKey;
      hasCalledApiRef.current = false;
      setCarrierPreviewData([]);
      setNoCarrierAssigned(false);
    }
  }, [isPopupVisible, carrier, requestKey]);

  // Fetch carrier preview when popup becomes visible - ONLY if carrier prop is null
  useEffect(() => {
    if (isPopupVisible && !carrier && requestKey !== 'none' && !hasCalledApiRef.current) {
      hasCalledApiRef.current = true;

      // Handle different API calls based on available props
      if (orderId) {
        // Use order ID API
        fetchCarrierPreviewByOrderId(orderId, {
          onSuccess: handleCarrierPreviewSuccess,
          onError: handleCarrierPreviewError,
        });
      } else if (rowUid && pickupAddressRegionUid) {
        // Use excel row API
        fetchCarrierPreviewByRowId(
          { rowKey: rowUid, pickupAddressRegionUid },
          {
            onSuccess: handleCarrierPreviewSuccess,
            onError: handleCarrierPreviewError,
          },
        );
      }
    }
  }, [isPopupVisible, carrier, orderId, rowUid, pickupAddressRegionUid, requestKey]);

  const handleCarrierPreviewSuccess = (response: any) => {
    // Reset noCarrierAssigned flag on success
    setNoCarrierAssigned(false);

    // Tag same-day carriers with serviceType
    const sameDayCarriers = (response.object.same_day_carriers || []).map((carrier: Carrier) => ({
      ...carrier,
      serviceType: ServiceTypeEnum.SAME_DAY,
    }));

    // Tag next-day carriers with serviceType
    const nextDayCarriers = (response.object.next_day_carriers || []).map((carrier: Carrier) => ({
      ...carrier,
      serviceType: ServiceTypeEnum.NEXT_DAY,
    }));

    // Combine both arrays
    const allCarriers = [...sameDayCarriers, ...nextDayCarriers];
    setCarrierPreviewData(allCarriers);
  };

  const handleCarrierPreviewError = (error: any) => {
    // Check if this is the specific NoCarrierMatchesException
    if (error?.response?.data?.code === NoCarrierMatchesExceptionError) {
      setNoCarrierAssigned(true);
      setCarrierPreviewData([]);
    } else {
      // For other errors, just clear the data
      setCarrierPreviewData([]);
    }
  };

  // Reset API call flag when popup closes
  useEffect(() => {
    if (!isPopupVisible) {
      hasCalledApiRef.current = false;
      setCarrierPreviewData(undefined);
    }
  }, [isPopupVisible]);

  const carrierPreviewLoading = carrierPreviewLoadingByOrderId || carrierPreviewLoadingByRowId;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        setCarrierPreviewData([]);
      }
    };
  }, []);

  return (
    <Popover
      destroyTooltipOnHide
      content={
        carrier ? (
          <div className='px-2 py-1' onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <span className='text-[13px] font-medium text-[rgb(57,57,60)]'>
              {carrier?.title ? String(t(normalizeToTranslationKey(carrier?.title))) : '---'}
            </span>
          </div>
        ) : noCarrierAssigned ? (
          <div className='px-2 py-1' onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <span className='text-[13px] text-[rgb(115,123,140)]'>
              {String(t('carrierHoverCell.noCarrierAssigned'))}
            </span>
          </div>
        ) : (
          <div
            className='w-[420px] py-3 px-3'
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <SmartCarrierMatch
              carrierList={carrierPreviewData ?? []}
              isLoading={carrierPreviewLoading}
            />
          </div>
        )
      }
      open={isPopupVisible && isHovering}
      trigger={[]}
      placement='right'
      overlayStyle={{ maxWidth: '460px' }}
      overlayInnerStyle={{ borderRadius: 16, padding: 0, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
    >
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className='m-auto block w-fit'
      >
        {carrier ? (
          <div className='w-8 h-8 rounded-full border border-[rgb(232,230,227)] overflow-hidden cursor-pointer bg-[rgb(245,245,243)]'>
            {carrier?.avatar?.abs_url ? (
              <img src={carrier.avatar.abs_url} alt={carrier.title} className='w-full h-full object-cover' />
            ) : (
              <div className='w-full h-full flex items-center justify-center text-[10px] font-bold text-[rgb(115,123,140)]'>
                {carrier?.title?.charAt(0) ?? '?'}
              </div>
            )}
          </div>
        ) : isHovering && carrierPreviewData === undefined ? (
          <SpinnerIcon
            className='animate-spin m-auto block cursor-pointer'
            width={16}
            height={16}
            color='#939496'
          />
        ) : (
          <InformationIcon
            className='m-auto block cursor-pointer'
            width={20}
            height={20}
            color='#939496'
          />
        )}
      </div>
    </Popover>
  );
};

export default CarrierHoverCell;
